import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, featuredMovies } from '@/mocks/home';

const categoryMap: Record<string, string> = {
  all: '全部',
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
  documentary: '纪录片',
  scifi: '科幻',
  action: '动作',
  comedy: '喜剧',
  romance: '爱情',
  horror: '悬疑',
  costume: '古装',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const currentName = categoryMap[currentSlug] || '全部';

  const filteredMovies = currentSlug === 'all'
    ? featuredMovies
    : featuredMovies.filter((m) => {
        const catMap: Record<string, string[]> = {
          movie: ['科幻', '奇幻', '古装', '悬疑', '动作', '爱情', '犯罪', '动画'],
          tv: ['古装', '悬疑', '爱情'],
          scifi: ['科幻'],
          action: ['动作', '犯罪'],
          romance: ['爱情'],
          horror: ['悬疑'],
          costume: ['古装'],
          anime: ['动画'],
        };
        const allowed = catMap[currentSlug];
        if (allowed) return allowed.includes(m.category);
        return false;
      });

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-4">
            <ol className="flex items-center gap-2 text-xs text-foreground-600 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li className="text-foreground-400">/</li>
              <li className="text-foreground-900 font-medium">{currentName}</li>
            </ol>
          </div>
        </nav>

        {/* Category Title */}
        <section className="bg-background-100 py-8">
          <div className="w-full px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center">
              {currentName}
              <span className="text-base font-normal text-foreground-500 ml-2">
                共{filteredMovies.length}部
              </span>
            </h1>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="w-full px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-1.5 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="py-10 md:py-14 bg-background-50 min-h-[400px]">
          <div className="w-full px-4 md:px-6">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-3">
                      <img
                        src={movie.image}
                        alt={movie.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                      <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs font-semibold px-2 py-0.5 rounded-md">
                        {movie.quality}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-accent-400 text-xs font-semibold flex items-center gap-0.5">
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-star-fill text-[10px]"></i>
                            </span>
                            {movie.rating}
                          </span>
                          <span className="text-white/60 text-xs">|</span>
                          <span className="text-white/80 text-xs">{movie.year}</span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {movie.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 line-clamp-1">
                      {movie.category}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-foreground-500">
                <span className="w-16 h-16 flex items-center justify-center mb-4">
                  <i className="ri-movie-2-line text-4xl"></i>
                </span>
                <p className="text-sm">该分类暂无影片，请选择其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://pianduoduo.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentName,
                item: `https://pianduoduo.com/category/${currentSlug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}